import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlatformClass, isLandscapeTooShort } from '@puzzlemania/ui';
import { PlayFullscreenButton } from './PlayFullscreenButton';

/**
 * Which landscape a route may keep. `null` ⇒ none (the portrait lock); a number ⇒ the route
 * has its own landscape layout and is walled off only below that height ("fit or rotate").
 */
export interface LandscapeAllowance {
  /** Game ids whose `/game/:id` route has a landscape layout of its own. */
  games: readonly string[];
  /** The landscape floor for those routes, in CSS px of height. */
  minHeight: number;
  /**
   * Offer "Play full screen" on those routes (`fullscreenButton`): a phone in
   * landscape under a browser bar is usually below the floor, and full screen
   * usually lifts it above.
   */
  fullscreen?: boolean;
}

const GAME_ROUTE = /^\/game\/([^/]+)\/?$/;

/** The landscape floor for this path, or `null` when the path is portrait-locked. */
export function landscapeFloorFor(
  pathname: string,
  allowance: LandscapeAllowance | undefined,
): number | null {
  if (!allowance) return null;
  const id = GAME_ROUTE.exec(pathname)?.[1];
  return id !== undefined && allowance.games.includes(id) ? allowance.minHeight : null;
}

/**
 * True only on a **mobile** device that must be asked to rotate. Desktop never is (it's
 * aspect-driven and allowed to be landscape). On a portrait-locked route that is any landscape
 * viewport; on a route with its own landscape layout, only one shorter than its floor.
 */
function shouldShow(floor: number | null): boolean {
  if (getPlatformClass() !== 'mobile' || typeof window === 'undefined') return false;
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  return floor === null ? viewport.width > viewport.height : isLandscapeTooShort(viewport, floor);
}

/**
 * Module 32 (WP-UI.6) — mobile is portrait-locked (product rule). Native builds enforce this at
 * the OS level (AndroidManifest `screenOrientation="portrait"`), but a mobile **web** browser
 * can't be OS-locked (notably iOS Safari), so when a phone browser is turned to landscape this
 * full-screen overlay asks the player to rotate back, hiding again on return to portrait.
 *
 * Desktop never sees it: the layout signal treats desktop landscape as a first-class mode, and
 * `getPlatformClass()` is `desktop` there — so the effect below attaches no listeners and the
 * overlay never renders. Mounted once at the app root, above every surface (lobby or a running
 * game HUD) via a top z-index.
 *
 * Exception — a game with a landscape layout of its own (the profile's `landscapeGames`; today
 * Blockway, Modules 47/89). On its route the device lock does not apply: the overlay follows the
 * same "fit or rotate" rule as the Bridge embed's `ShortScreenHint` and appears only while the
 * landscape area is shorter than `landscapeMinHeight`. Leaving that game for the Lobby in
 * landscape brings the lock back, since the Lobby and Water Sort have no landscape layout.
 */
export function RotateToPortraitOverlay({
  pathname = '/',
  landscape,
}: {
  /** The current route, so the overlay knows whether it is on a landscape-capable game. */
  pathname?: string;
  landscape?: LandscapeAllowance | undefined;
} = {}) {
  const { t } = useTranslation();
  const floor = landscapeFloorFor(pathname, landscape);
  const [show, setShow] = useState(() => shouldShow(floor));

  useEffect(() => {
    // Only mobile can enter the rotated state; desktop attaches nothing.
    if (getPlatformClass() !== 'mobile') return;
    const update = () => setShow(shouldShow(floor));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [floor]);

  if (!show) return null;

  return (
    <div
      className="rotate-overlay"
      role="alertdialog"
      aria-label={t('layout.rotateToPortrait')}
      data-testid="rotate-overlay"
    >
      <div className="rotate-overlay__content">
        <div className="rotate-overlay__icon" aria-hidden="true">
          🔄
        </div>
        <p className="rotate-overlay__text">{t('layout.rotateToPortrait')}</p>
        {/* Enter only where a landscape layout exists: on a portrait-locked
            route full screen is still landscape and would not help. Exit is
            offered everywhere, so a player is never stuck in full screen. */}
        {landscape?.fullscreen === true && <PlayFullscreenButton canEnter={floor !== null} />}
      </div>
    </div>
  );
}
